import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WaveSourcePicker, type WaveSource } from "@/components/admin_dashboard/FollowUpWavesEditor";
import {
  CheckRow,
  HeadingWithTip,
  MAX_INSTRUCTIONS_LENGTH,
  MessageContentFields,
  NumberField,
  useServerWorkflowSettings,
  useStoredSettings,
  type MessageContentValue,
  type WorkflowMessageSettingsRow,
} from "@/components/admin_dashboard/WorkflowSettingsFields";

// Settings for workflows that are still "Coming soon". They are saved in this
// browser only (see useStoredSettings) and nothing runs from them yet.

interface WorkflowSettingsProps {
  businessId?: string;
  sources: WaveSource[];
  sourcesLoading: boolean;
}

interface OfferPickerProps {
  id: string;
  offers: WaveSource[];
  loading: boolean;
  selectedId: string | null;
  onChange: (offerId: string | null) => void;
}

/** Picks one of the offers written in Knowledge → Offers. */
const OfferPicker = ({ id, offers, loading, selectedId, onChange }: OfferPickerProps) => (
  <WaveSourcePicker
    waveId={id}
    label="Offer"
    hint="Offers come from Knowledge → Offers."
    sources={offers}
    loading={loading}
    selectedId={selectedId}
    disabled={false}
    placeholder="Choose an offer"
    popoverTitle="Offers"
    emptyText="You haven't added any offers yet. Add one in Knowledge → Offers."
    onChange={onChange}
  />
);

const days = (count: number) => (count === 1 ? "day" : "days");
const defaultContent: MessageContentValue = { contentMode: "ai", instructions: "", sourceId: null };

interface TrialToPaidSettings extends MessageContentValue {
  trialType: "lesson" | "package";
  hoursAfterLesson: number;
  daysBeforeEnd: number;
  stopOnDecline: boolean;
  stopOnStaffReply: boolean;
}

export const TrialToPaidSettings = ({ businessId, sources, sourcesLoading }: WorkflowSettingsProps) => {
  const [settings, update] = useStoredSettings<TrialToPaidSettings>(
    `dibs:workflow-trial-to-paid:${businessId ?? "guest"}`,
    { ...defaultContent, trialType: "lesson", hoursAfterLesson: 2, daysBeforeEnd: 2, stopOnDecline: true, stopOnStaffReply: true },
  );

  return (
    <div className="space-y-6">
      <div>
        <p id="trial-type-label" className="text-sm font-semibold text-foreground">Trial type</p>
        <RadioGroup
          className="mt-3 gap-2"
          value={settings.trialType}
          aria-labelledby="trial-type-label"
          onValueChange={(trialType) => update({ trialType: trialType as TrialToPaidSettings["trialType"] })}
        >
          {([
            ["lesson", "A single trial lesson", "Dibs follows up shortly after the lesson."],
            ["package", "A trial package or period", "Dibs follows up before the trial runs out."],
          ] as const).map(([value, label, hint]) => (
            <label
              key={value}
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                settings.trialType === value ? "border-foreground/35 bg-muted/60" : "border-border hover:bg-muted/40"
              }`}
            >
              <RadioGroupItem value={value} aria-label={label} className="mt-0.5 border-foreground text-foreground" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">{label}</span>
                <span className="mt-1 block text-xs leading-4 text-muted-foreground">{hint}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div>
        <HeadingWithTip
          id="trial-timing-heading"
          title="Timing"
          tip={settings.trialType === "lesson"
            ? "Dibs follows up once, this long after the customer's trial lesson ends."
            : "Dibs follows up once, this many days before a customer's trial ends."}
        />
        <div className="mt-3">
          {settings.trialType === "lesson" ? (
            <NumberField
              id="trial-timing-lesson"
              labelledBy="trial-timing-heading"
              value={settings.hoursAfterLesson}
              min={1}
              max={72}
              suffix={`${settings.hoursAfterLesson === 1 ? "hour" : "hours"} after the trial lesson ends`}
              onCommit={(hoursAfterLesson) => update({ hoursAfterLesson })}
            />
          ) : (
            <NumberField
              id="trial-timing-package"
              labelledBy="trial-timing-heading"
              value={settings.daysBeforeEnd}
              min={1}
              max={30}
              suffix={`${days(settings.daysBeforeEnd)} before the trial ends`}
              onCommit={(daysBeforeEnd) => update({ daysBeforeEnd })}
            />
          )}
        </div>
      </div>

      <MessageContentFields
        id="trial"
        noun="follow-up"
        tip="Choose how Dibs invites trial members to choose a plan."
        instructionsPlaceholder="e.g. Mention the monthly plan and offer to book their next class."
        value={settings}
        sources={sources}
        sourcesLoading={sourcesLoading}
        onChange={update}
      />

      <div>
        <HeadingWithTip
          id="trial-stop-heading"
          title="Stop following up after"
          tip="Dibs stops as soon as any of these happen."
        />
        <div className="mt-4 space-y-3">
          <CheckRow
            id="trial-stop-purchase"
            label="The customer buys a plan"
            hint="Always on. There's nothing more to chase once they've paid."
            checked
            disabled
          />
          <CheckRow
            id="trial-stop-decline"
            label="The customer says no"
            hint="Includes “not interested” and “stop messaging me”."
            checked={settings.stopOnDecline}
            onChange={(stopOnDecline) => update({ stopOnDecline })}
          />
          <CheckRow
            id="trial-stop-staff"
            label="Your team replies"
            hint="Staff took over the chat, so Dibs stays out of it."
            checked={settings.stopOnStaffReply}
            onChange={(stopOnStaffReply) => update({ stopOnStaffReply })}
          />
        </div>
      </div>
    </div>
  );
};

interface DormantWinbackSettings extends MessageContentValue {
  inactiveDays: number;
  cooldownDays: number;
}

export const DormantWinbackSettings = ({ businessId, sources, sourcesLoading }: WorkflowSettingsProps) => {
  const [settings, update] = useStoredSettings<DormantWinbackSettings>(
    `dibs:workflow-dormant-winback:${businessId ?? "guest"}`,
    { ...defaultContent, inactiveDays: 60, cooldownDays: 90 },
  );

  return (
    <div className="space-y-6">
      <div>
        <HeadingWithTip
          id="winback-who-heading"
          title="Who to message"
          tip="Customers who have booked with you before but haven't booked again since."
        />
        <div className="mt-3">
          <NumberField
            id="winback-inactive"
            labelledBy="winback-who-heading"
            prefix="Haven't booked in"
            value={settings.inactiveDays}
            min={14}
            max={365}
            suffix={days(settings.inactiveDays)}
            onCommit={(inactiveDays) => update({ inactiveDays })}
          />
        </div>
      </div>

      <div>
        <HeadingWithTip
          id="winback-frequency-heading"
          title="How often"
          tip="Stops the same customer from being messaged again too soon."
        />
        <div className="mt-3">
          <NumberField
            id="winback-cooldown"
            labelledBy="winback-frequency-heading"
            prefix="At most once every"
            value={settings.cooldownDays}
            min={30}
            max={365}
            suffix={days(settings.cooldownDays)}
            onCommit={(cooldownDays) => update({ cooldownDays })}
          />
        </div>
      </div>

      <MessageContentFields
        id="winback"
        noun="message"
        tip="Choose how Dibs invites customers back."
        instructionsPlaceholder="e.g. Say we miss them and mention the new weekend classes."
        value={settings}
        sources={sources}
        sourcesLoading={sourcesLoading}
        onChange={update}
      />
    </div>
  );
};

export interface ComplaintRecoverySettings extends MessageContentValue {
  notifyStaff: boolean;
  pauseOtherMessages: boolean;
  offerGesture: boolean;
  gestureOfferId: string | null;
}

const DEFAULT_COMPLAINT_RECOVERY_SETTINGS: ComplaintRecoverySettings = {
  ...defaultContent,
  notifyStaff: true,
  pauseOtherMessages: true,
  offerGesture: false,
  gestureOfferId: null,
};

export function complaintSettingsFromServer(
  row: WorkflowMessageSettingsRow,
  initial: ComplaintRecoverySettings = DEFAULT_COMPLAINT_RECOVERY_SETTINGS,
): ComplaintRecoverySettings {
  const cfg = (row.config && typeof row.config === "object" && !Array.isArray(row.config))
    ? row.config
    : {};
  const contentMode = row.content_mode === "template" ? "template" : "ai";
  const templateIds = Array.isArray(row.template_source_ids) ? row.template_source_ids : [];
  const aiIds = Array.isArray(row.ai_source_ids) ? row.ai_source_ids : [];
  const sourceId = contentMode === "template" ? (templateIds[0] ?? null) : (aiIds[0] ?? null);
  return {
    contentMode,
    instructions: typeof row.ai_instructions === "string"
      ? row.ai_instructions.slice(0, MAX_INSTRUCTIONS_LENGTH)
      : initial.instructions,
    sourceId,
    notifyStaff: typeof cfg.notify_staff === "boolean" ? cfg.notify_staff : initial.notifyStaff,
    pauseOtherMessages: typeof cfg.pause_other_messages === "boolean"
      ? cfg.pause_other_messages
      : initial.pauseOtherMessages,
    offerGesture: typeof cfg.offer_gesture === "boolean" ? cfg.offer_gesture : initial.offerGesture,
    gestureOfferId: typeof cfg.gesture_offer_id === "string" && cfg.gesture_offer_id
      ? cfg.gesture_offer_id
      : null,
  };
}

export function complaintSettingsToServer(settings: ComplaintRecoverySettings): WorkflowMessageSettingsRow {
  return {
    content_mode: settings.contentMode,
    ai_instructions: settings.instructions.slice(0, MAX_INSTRUCTIONS_LENGTH),
    ai_source_ids: settings.contentMode === "ai" && settings.sourceId ? [settings.sourceId] : [],
    template_source_ids: settings.contentMode === "template" && settings.sourceId ? [settings.sourceId] : [],
    config: {
      notify_staff: settings.notifyStaff,
      pause_other_messages: settings.pauseOtherMessages,
      offer_gesture: settings.offerGesture,
      gesture_offer_id: settings.offerGesture ? settings.gestureOfferId : null,
    },
  };
}

export const ComplaintRecoverySettings = ({
  businessId,
  sources,
  sourcesLoading,
  offers,
}: WorkflowSettingsProps & { offers: WaveSource[] }) => {
  const [settings, update] = useServerWorkflowSettings<ComplaintRecoverySettings>({
    businessId,
    workflowId: "complaint-recovery",
    storageKey: `dibs:workflow-complaint-recovery:${businessId ?? "guest"}`,
    initial: DEFAULT_COMPLAINT_RECOVERY_SETTINGS,
    fromServer: complaintSettingsFromServer,
    toServer: complaintSettingsToServer,
  });

  return (
    <div className="space-y-6">
      <MessageContentFields
        id="complaint"
        noun="reply"
        tip="Choose how Dibs responds when a customer complains."
        instructionsPlaceholder="e.g. Be warm, apologise once, and say a team member will be in touch today."
        value={settings}
        sources={sources}
        sourcesLoading={sourcesLoading}
        onChange={update}
      />

      <div>
        <HeadingWithTip
          id="complaint-actions-heading"
          title="When a complaint is spotted"
          tip="What else Dibs does as soon as it detects an unhappy customer."
        />
        <div className="mt-4 space-y-3">
          <CheckRow
            id="complaint-notify"
            label="Notify your team straight away"
            hint="Your team gets an alert with the conversation so they can step in."
            checked={settings.notifyStaff}
            onChange={(notifyStaff) => update({ notifyStaff })}
          />
          <CheckRow
            id="complaint-pause"
            label="Pause other automated messages"
            hint="Stops promotions and follow-ups to this customer until the complaint is resolved."
            checked={settings.pauseOtherMessages}
            onChange={(pauseOtherMessages) => update({ pauseOtherMessages })}
          />
        </div>
      </div>

      <div>
        <HeadingWithTip
          id="complaint-gesture-heading"
          title="Goodwill gesture"
          tip="Dibs only offers something you've set up in Knowledge → Offers. It never makes up offers."
        />
        <div className="mt-4 space-y-3">
          <CheckRow
            id="complaint-gesture"
            label="Offer a goodwill gesture"
            hint="Dibs can mention an offer from your Knowledge when it apologises."
            checked={settings.offerGesture}
            onChange={(offerGesture) => update({ offerGesture, ...(offerGesture ? {} : { gestureOfferId: null }) })}
          />
          {settings.offerGesture && (
            <OfferPicker
              id="complaint-gesture"
              offers={offers}
              loading={sourcesLoading}
              selectedId={settings.gestureOfferId}
              onChange={(gestureOfferId) => update({ gestureOfferId })}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface BirthdayMilestoneSettings extends MessageContentValue {
  birthday: boolean;
  milestone: boolean;
  milestoneEvery: number;
  anniversary: boolean;
  includeOffer: boolean;
  offerId: string | null;
  sendHour: string;
}

const SEND_HOURS = [8, 9, 10, 12, 15, 18] as const;
const formatHour = (hour: number) => `${hour % 12 || 12}:00 ${hour < 12 ? "am" : "pm"}`;

export const BirthdayMilestoneSettings = ({
  businessId,
  sources,
  sourcesLoading,
  offers,
}: WorkflowSettingsProps & { offers: WaveSource[] }) => {
  const [settings, update] = useStoredSettings<BirthdayMilestoneSettings>(
    `dibs:workflow-birthday-milestone-message:${businessId ?? "guest"}`,
    { ...defaultContent, birthday: true, milestone: true, milestoneEvery: 10, anniversary: false, includeOffer: false, offerId: null, sendHour: "9" },
  );

  return (
    <div className="space-y-6">
      <div>
        <HeadingWithTip
          id="occasion-heading"
          title="Occasions"
          tip="When Dibs sends a message. Birthdays need the customer's date of birth on their profile."
        />
        <div className="mt-4 space-y-3">
          <CheckRow
            id="occasion-birthday"
            label="Birthday"
            hint="Sent on the customer's birthday."
            checked={settings.birthday}
            onChange={(birthday) => update({ birthday })}
          />
          <CheckRow
            id="occasion-milestone"
            label="Class milestone"
            hint="Sent when a customer reaches a round number of classes."
            checked={settings.milestone}
            onChange={(milestone) => update({ milestone })}
          />
          {settings.milestone && (
            <div className="pl-7">
              <span id="occasion-milestone-label" className="sr-only">Milestone frequency</span>
              <NumberField
                id="occasion-milestone-every"
                labelledBy="occasion-milestone-label"
                prefix="Every"
                value={settings.milestoneEvery}
                min={1}
                max={100}
                suffix="classes"
                onCommit={(milestoneEvery) => update({ milestoneEvery })}
              />
            </div>
          )}
          <CheckRow
            id="occasion-anniversary"
            label="Membership anniversary"
            hint="Sent on the anniversary of the customer's first booking."
            checked={settings.anniversary}
            onChange={(anniversary) => update({ anniversary })}
          />
        </div>
      </div>

      <div>
        <HeadingWithTip
          id="occasion-time-heading"
          title="Send time"
          tip="The time of day the message goes out, in your outlet's local time."
        />
        <Select value={settings.sendHour} onValueChange={(sendHour) => update({ sendHour })}>
          <SelectTrigger aria-labelledby="occasion-time-heading" className="mt-3 h-9 w-full text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEND_HOURS.map((hour) => (
              <SelectItem key={hour} value={String(hour)}>{formatHour(hour)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <MessageContentFields
        id="occasion"
        noun="message"
        tip="Choose how Dibs writes the message."
        instructionsPlaceholder="e.g. Keep it short and warm, and sign off with the instructor's name."
        value={settings}
        sources={sources}
        sourcesLoading={sourcesLoading}
        onChange={update}
      />

      <div>
        <HeadingWithTip
          id="occasion-offer-heading"
          title="Offer"
          tip="Dibs only mentions an offer you've set up in Knowledge → Offers. It never makes up offers."
        />
        <div className="mt-4 space-y-3">
          <CheckRow
            id="occasion-offer"
            label="Include an offer"
            hint="Dibs mentions the chosen offer in the message, for example a free class on their birthday."
            checked={settings.includeOffer}
            onChange={(includeOffer) => update({ includeOffer, ...(includeOffer ? {} : { offerId: null }) })}
          />
          {settings.includeOffer && (
            <OfferPicker
              id="occasion-offer"
              offers={offers}
              loading={sourcesLoading}
              selectedId={settings.offerId}
              onChange={(offerId) => update({ offerId })}
            />
          )}
        </div>
      </div>
    </div>
  );
};
